import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import type { DecoratedEntry } from '@/pages/Schedule/types';
import { getEntryCategoryColors } from '@/pages/Schedule/utils/entry';
import {
  Flex,
  type FlexProps,
  IconButton,
  Tooltip,
  type RadioProps,
  Icon,
  useColorMode,
} from '@/ui';
import { useCategorySelectContext } from '../hooks/useCategorySelectContext';
import { Row } from './Row';

type Category = NonNullable<DecoratedEntry['category']>;

type Props = Omit<FlexProps, 'onSelect'> & {
  category: Category;
  radioProps?: RadioProps;
  isChecked: boolean;
  onSelect: (category: Category) => void;
};

export const CategoryRow = ({
  category,
  isChecked,
  radioProps,
  onSelect,
}: Props) => {
  const { text, color } = category;

  const { editCategoryClick } = useCategorySelectContext();
  const { t } = useTranslation('categorySelect');
  // Sorting is pointer-only (CategoryPanel registers only a PointerSensor), so
  // dnd-kit's `attributes` (role="button", tabIndex, sortable instructions) are
  // intentionally not spread — they'd advertise keyboard dragging that doesn't
  // exist and nest interactive roles inside the row.
  const { listeners, setActivatorNodeRef, setNodeRef, transform, transition } =
    useSortable({ id: category.id });
  const { colorMode } = useColorMode();

  const colors = getEntryCategoryColors(color, colorMode);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Row
      _hover={{ bg: colors.hoverBackgroundColor }}
      bg={colors.backgroundColor}
      borderWidth="2px"
      color="customgray.dark"
      flex="1"
      isChecked={isChecked}
      paddingLeft={3}
      paddingRight={1}
      radioProps={radioProps}
      ref={setNodeRef}
      style={style}
      transition="background-color 0.2s ease"
      onClick={() => onSelect(category)}
    >
      {/* Dragging is confined to this handle rather than the whole row. A row
          that is both the drag activator and the select target can't tell the
          two apart on touch: without `touch-action: none` the browser claims
          the gesture as a scroll and cancels the pointer, so dnd-kit never
          activates and the drop lands as a plain tap — selecting the category
          and dismissing the sheet (AH-2579). Scoping `touch-action` to the
          handle also leaves the list itself scrollable. */}
      <IconButton
        _hover={{ bg: colors.hoverBackgroundColor }}
        aria-label={t('reorder')}
        as="div"
        color="customgray.dark"
        cursor="grab"
        icon={<Icon icon="GripVertical" size="3.5" />}
        ml={-2}
        mr={1}
        ref={setActivatorNodeRef}
        size="xs"
        sx={{ touchAction: 'none' }}
        variant="dark"
        {...listeners}
        onClick={(event) => event.stopPropagation()}
      />

      <Flex
        align="center"
        flex="1"
        h="100%"
        mr={2}
        overflow="hidden"
        whiteSpace="nowrap"
      >
        {text}
      </Flex>

      <Tooltip label={t('edit_tooltip')} maxW="300px">
        <IconButton
          aria-label={t('edit')}
          as="div"
          color="customgray.dark"
          icon={<Icon icon="MoreHorizontal" size="3.5" />}
          size="xs"
          variant="dark"
          _hover={{
            bg: colors.hoverBackgroundColor,
            svg: {
              fill: 'black',
            },
          }}
          onClick={(event) => {
            event.stopPropagation();
            editCategoryClick(category);
          }}
        />
      </Tooltip>
    </Row>
  );
};
